import { AlertTriangle, Clock, Info, Lock } from "lucide-react";
import { SELLER_PAYOUT_HOURS } from "@/lib/businessDays";
import { cn } from "@/lib/utils";

/**
 * Bloc pédagogique : séquestre, validation RDV, délai, anti-détournement.
 */
export default function EscrowPaymentExplainer({ className }) {
  const payoutHours = SELLER_PAYOUT_HOURS;

  return (
    <div className={cn("space-y-4", className)}>
      {/* Séquestre commun, puis deux issues possibles (si / si) */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3 sm:px-5">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-200/80 text-[#2D7FF9]">
              <Lock className="h-4 w-4" aria-hidden />
            </span>
            <div>
              <p className="font-display text-sm font-semibold text-slate-900">Comment ça marche</p>
              <p className="mt-0.5 text-xs text-slate-600">Pendant l’appel, puis selon l’issue du RDV</p>
            </div>
          </div>
        </div>
        <div className="space-y-3 px-4 py-4 sm:px-5 sm:py-5">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="font-display text-sm font-semibold text-slate-900">Pendant l’appel</p>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
              Le paiement est <strong className="font-semibold text-slate-900">bloqué</strong> sur la plateforme (séquestre).
            </p>
          </div>
          <p className="text-center text-[11px] font-medium uppercase tracking-wide text-slate-500">Ensuite, l’un ou l’autre</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4">
              <p className="font-display text-sm font-semibold text-emerald-950">Si le RDV est confirmé</p>
              <p className="mt-2 text-sm leading-relaxed text-emerald-950/90">
                Vous êtes <strong className="font-semibold text-emerald-950">débité</strong> et le vendeur est rémunéré selon les règles affichées.
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-4">
              <p className="font-display text-sm font-semibold text-slate-900">Si le RDV est refusé</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Si le refus est <strong className="font-semibold text-slate-900">justifié</strong>, vous êtes <strong className="font-semibold text-slate-900">remboursé</strong>.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Concurrence */}
      <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 sm:p-5">
        <div className="flex gap-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white ring-1 ring-slate-200">
            <Info className="h-5 w-5 text-[#2D7FF9]" aria-hidden />
          </span>
          <div className="min-w-0 space-y-1">
            <p className="font-display text-sm font-semibold text-slate-900">Premier arrivé, premier servi</p>
            <p className="text-sm leading-relaxed text-slate-600">
              D’autres acheteurs peuvent être sur le même dossier. Pour sécuriser le RDV, appelez{" "}
              <strong className="font-semibold text-slate-800">au plus vite</strong>. Une fois le RDV confirmé sur la
              plateforme, <strong className="font-semibold text-slate-800">les autres n’y ont plus accès</strong>.
            </p>
          </div>
        </div>
      </div>

      {/* Délai */}
      <div className="rounded-xl border border-sky-200 bg-sky-50/70 p-4 sm:p-5">
        <div className="flex gap-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
            <Clock className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0 space-y-2">
            <p className="font-display text-sm font-semibold text-sky-950">
              Délai {payoutHours} h — organisation du rendez-vous
            </p>
            <p className="text-sm leading-relaxed text-sky-950/85">
              Il est fortement conseillé de convenir d’un rendez-vous avec le client sous{" "}
              <strong>{payoutHours} heures</strong> après votre achat. Après validation du RDV, les fonds peuvent être versés
              au vendeur selon le délai de <strong>{payoutHours} heures</strong> prévu par la plateforme. Au-delà, vous ne
              pourrez plus vous prévaloir des recours habituels sur cette transaction.
            </p>
          </div>
        </div>
      </div>

      {/* Anti-détournement */}
      <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4 sm:p-5">
        <div className="flex gap-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-800">
            <AlertTriangle className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0 space-y-1">
            <p className="font-display text-sm font-semibold text-amber-950">Règle anti-détournement</p>
            <p className="text-sm leading-relaxed text-amber-950/90">
              Toute tentative d’échange de coordonnées (nom, téléphone, adresse…) avant confirmation déclenche automatiquement le{" "}
              <strong>paiement au vendeur</strong>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
